/**
 * TaskWatcher.tsx — Feature 23: Task Watching / Subscriptions
 * Lets users subscribe to a task to receive notifications on any change.
 * Uses PATCH /tasks/{id}/watch and PATCH /tasks/{id}/unwatch endpoints.
 */
import React, { useEffect, useState } from 'react';
import { BellRing, Bell } from 'lucide-react';
import { apiClient } from '../packages/api-client';

interface Props {
    taskId: string;
    /** Initial watched state — pass from parent task object if available */
    initiallyWatching?: boolean;
}

export function TaskWatcher({ taskId, initiallyWatching = false }: Props) {
    const [watching, setWatching] = useState(initiallyWatching);
    const [loading, setLoading] = useState(false);

    // Fetch current watch state if not provided by parent
    useEffect(() => {
        apiClient.get<{ watching: boolean }>(`/tasks/${taskId}/watch`)
            .then(r => setWatching(r.data.watching))
            .catch(() => {});
    }, [taskId]);

    async function toggle() {
        setLoading(true);
        try {
            if (watching) {
                await apiClient.delete(`/tasks/${taskId}/watch`);
            } else {
                await apiClient.post(`/tasks/${taskId}/watch`, {});
            }
            setWatching(w => !w);
        } catch {
            // silent fail — show toast in production
        } finally {
            setLoading(false);
        }
    }

    return (
        <button
            className={`btn btn-sm ${watching ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={toggle}
            disabled={loading}
            title={watching ? 'Unwatch this task (stop getting notifications)' : 'Watch this task (get notified of changes)'}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
            {loading ? '…' : watching ? (<><BellRing size={14} aria-hidden="true" /> Watching</>) : (<><Bell size={14} aria-hidden="true" /> Watch</>)}
        </button>
    );
}
